import React from "react";
import {
  Box,
  Paper,
  Typography,
  Divider,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../stores/useAuthStore";
import useCustomerStore from "../stores/useCustomerStore";

function InstallmentSelectionPage() {
  const navigate = useNavigate();

  // Get data from auth store
  const { getInstallmentData, selectedInstallment, setSelectedInstallment, amount, setAmount } = useAuthStore();
    const { adminInfo } = useAuthStore();
    const isAdmin = adminInfo?.isAdmin;
  const authData = getInstallmentData(); // returns { minimumLimitForInstallment, installmentCount }
  
  // Get data from customer store (admin scenario)
  const customerData = useCustomerStore.getState().getInstallmentData(); // returns { installmentCount }

  // Determine the final installment count: if customerData.installmentCount exists, use it; otherwise, fallback to authData.installmentCount.
  const finalInstallmentCount = isAdmin? customerData.installmentCount : authData.installmentCount;
  console.log("auth data",authData.installmentCount);
  console.log("Final Installment Count:", finalInstallmentCount);
  console.log("Minimum Limit For Installment:", authData.minimumLimitForInstallment);

  // Convert the amount string to a number.
  const numericAmount = Number(amount);

  // Default options for installment counts.
  const allOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 12];

  // Determine possible installments:
  // If finalInstallmentCount is 0/null and the numeric amount is greater than or equal to the minimum limit, show all options.
  // Otherwise, if the numeric amount is less than the minimum limit, show only [1].
  // If a valid finalInstallmentCount exists, only allow options up to that number.
  const possibleInstallments = React.useMemo(() => {
    if ((Number(finalInstallmentCount) === 0 || finalInstallmentCount === null || finalInstallmentCount === undefined) 
        && numericAmount >= authData.minimumLimitForInstallment) {
      return allOptions;
    }

    if (!numericAmount || numericAmount < authData.minimumLimitForInstallment) {
      return [];
    }

    return allOptions.filter((x) => x <= finalInstallmentCount);
  }, [finalInstallmentCount, numericAmount, authData.minimumLimitForInstallment, allOptions]);

  // Set default selected installment if none is selected.
  React.useEffect(() => {
    if (possibleInstallments.length > 0 && selectedInstallment === null) {
      setSelectedInstallment(possibleInstallments[0]);
    }
  }, [possibleInstallments, selectedInstallment, setSelectedInstallment]);

  // Navigate to payment page with selected installment and amount.
  const handleNext = () => {
    if (selectedInstallment) {
      navigate("/payment", {
        state: {
          amount: numericAmount,
          selectedInstallment,
        },
      });
    }
  };

  return (
    <Box sx={{ width: "100%", minHeight: "70vh", py: 4 }}>
      {/* Stepper */}
      <Box sx={{ width: { xs: "90%", md: "60%" }, mb: 3, mx: "auto" }}>
        <Stepper activeStep={0} alternativeLabel>
          <Step>
            <StepLabel>Taksit Seçimi</StepLabel>
          </Step>
          <Step>
            <StepLabel>Ödeme Detayları</StepLabel>
          </Step>
          <Step>
            <StepLabel>Tamamla</StepLabel>
          </Step>
        </Stepper>
      </Box>

      {/* Content */}
      <Box sx={{ width: { xs: "90%", md: "60%" }, mx: "auto" }}>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Taksit Seçimi
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {/* Amount Input */}
          <TextField
            label="Tutar (₺)"
            fullWidth
            value={amount}
            onChange={(e) => {
              const val = e.target.value;
              // Allow only digits to prevent leading zeros.
              if (/^\d*$/.test(val)) setAmount(val);
            }}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
            sx={{ mb: 3 }}
          />
          {numericAmount > 0 && numericAmount < authData.minimumLimitForInstallment && (
  <Alert severity="error" sx={{ mb: 2 }}>
    Belirtilen limitten düşük işlem yapılamaz. Minimum limit: ₺{authData.minimumLimitForInstallment}
  </Alert>
)}

          {/* Installment Options */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {possibleInstallments.map((count) => (
              <Box
                key={count}
                onClick={() => setSelectedInstallment(count)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  border: "1px solid",
                  borderColor: selectedInstallment === count ? "primary.main" : "grey.300",
                  borderRadius: 1,
                  p: 1,
                  transition: "background-color 0.3s",
                  "&:hover": {
                    backgroundColor: "grey.100",
                  },
                }}
              >
                {/* Circle indicator for selection */}
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    border: "2px solid",
                    borderColor: selectedInstallment === count ? "primary.main" : "grey.400",
                    mr: 1,
                    backgroundColor: selectedInstallment === count ? "primary.main" : "transparent",
                  }}
                />
                <Typography variant="body1">
                  {count === 1 ? "Tek Çekim" : `${count} Taksit`}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Selected Info */}
          {selectedInstallment && (
            <Box textAlign="center" mt={3}>
              <Typography variant="subtitle1" color="textSecondary">
                Seçilen Taksit: <strong>{selectedInstallment}</strong>
              </Typography>
            </Box>
          )}

          {/* Continue Button */}
          <Box textAlign="center" mt={4}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleNext}
              disabled={numericAmount <= 0 || isNaN(numericAmount)||
                numericAmount < authData.minimumLimitForInstallment}
            >
              Devam Et
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

export default InstallmentSelectionPage;
